import React, { useEffect, useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';
import { useFormValidation } from '@gjensidige/se-form-validation';
import type { ValidationRule, FieldValidation, FieldType } from '@gjensidige/se-form-validation';

import { getAddressDetails } from '../api-config/index';
import { WidgetProps } from '../interfaces/Types';
import { AppHelper } from '../utils/AppHelper';
interface InputWidgetProps {
  enonicData: WidgetProps;
}

export const InputWidgetContainer: React.FC<InputWidgetProps> = ({
  enonicData
}: InputWidgetProps) => {
  const [formData, setFormData] = useState({
    ssn: '',
    zipCode: '',
    phone: '',
    houseType: '',
    address: ''
  });
  const [addressFromService, setAddressFromService] = useState({
    calculationStatus: {
      error: false
    },
    customerName: '',
    adress: '',
    zipCode: '',
    cpr: ''
  });

  useEffect(() => {
    if (enonicData?.selectedHouseType === 'hyresratt' || enonicData?.selectedHouseType === 'bostadsratt') {
      const houseTypeValue = enonicData.selectedHouseType === 'hyresratt' ? '1' : '2';
      setFormData(prev => ({ ...prev, houseType: houseTypeValue }));
    }
  }, [enonicData?.selectedHouseType]);

  const MIN_AGE = 18;
  const NEXT_PAGE_URL_PATH = '/links/webshop-home';

  const fieldDefinitions = [
    {
      fieldType: 'ssn',
      fieldName: 'Personnummer',
      validation: {
        rules: ['required', 'ssn', 'ssnAge'] as ValidationRule[],
        validationParams: { minAge: MIN_AGE },
        validateOnChange: true
      },
      ui: {
        id: 'cpr-number',
        labelText: enonicData.cprNumberLabel,
        name: 'input-cprNumber',
        className: 'mt-1',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'ssn',
        helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
        isHelpTextAlwaysVisible: true,
        inputMode: 'numeric' as const
      }
    },
    {
      fieldType: 'zipCode',
      fieldName: 'Postnummer',
      validation: {
        rules: ['required', 'zipCode'] as ValidationRule[]
      },
      ui: {
        id: 'post-number',
        labelText: enonicData.zipCodeLabel,
        name: 'postNumber',
        placeholder: '',
        className: '',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'zip-code',
        helpText: '',
        inputMode: 'numeric' as const,
        autoCompleteLabel: 'postal-code'
      }
    },
    {
      fieldType: 'address',
      fieldName: 'Adress',
      validation: {
        rules: ['required', 'address'] as ValidationRule[]
      },
      ui: {
        id: 'gatuadress',
        labelText: enonicData.addressLabel,
        name: 'input-gatuadress',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'street-address',
        autoCompleteLabel: 'street-address'
      }
    },
    {
      fieldType: 'phone',
      fieldName: 'Mobil',
      validation: {
        rules: ['phone'] as ValidationRule[]
      },
      ui: {
        id: 'phone-number',
        labelText: 'Mobil (frivilligt)',
        name: 'input-phoneNumber',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'phone-number',
        helpText: AppHelper.phoneNrHelpText,
        helpTextToggleLabel: 'Visa hjälptext för Mobil',
        helpTextCloseLabel: 'Stäng hjälptext för Mobil',
        inputMode: 'numeric' as const,
        autoCompleteLabel: 'tel'
      }
    }
  ] as const;

  const houseTypeOptions = [
    {
      id: 'rent',
      labelText: enonicData.tenancyLabel,
      value: '1'
    },
    {
      id: 'owned',
      labelText: enonicData.ownedLabel,
      value: '2'
    }
  ];

  const fieldConfigs: FieldValidation[] = [
    {
      fieldType: 'houseType' as FieldType,
      fieldName: 'Bostadstyp',
      rules: ['required'] as ValidationRule[],
      customMessage: 'Du måste välja en bostadstyp.'
    },
    ...fieldDefinitions.map(field => ({
      fieldType: field.fieldType,
      fieldName: field.fieldName,
      rules: field.validation.rules,
      ...('validationParams' in field.validation && field.validation.validationParams && {
        validationParams: field.validation.validationParams
      }),
      ...('validateOnChange' in field.validation && field.validation.validateOnChange && {
        validateOnChange: field.validation.validateOnChange,
      })
    }))
  ];

  const {
    refs,
    handleBlur,
    handleChange,
    validateAll,
    getFieldError,
    hasFieldError,
  } = useFormValidation(fieldConfigs);

  const createChangeHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'zipCode') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'phone') {
      value = value.trim();
    }

    setFormData(prev => ({ ...prev, [fieldType]: value }));

    if (fieldType === 'ssn') {
      const validationResult = handleChange(fieldType, value);
      if (validationResult.isValid) {
        getAddressFromSSN(value);
      }
    } else if (fieldType === 'zipCode') {
      const validationResult = handleChange(fieldType, value);
      if (validationResult.isValid && addressFromService.zipCode === value) {
        setFormData(prev => ({ ...prev, address: addressFromService.adress }));
      }
    } else {
      handleChange(fieldType, value);
    }
  };

  const createBlurHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    // Field-specific processing
    if (fieldType === 'houseType' as FieldType) {
      value = formData.houseType || '';
    } else if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'zipCode') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'phone') {
      value = value.trim();
    }

    const validationResult = handleBlur(fieldType, value);

    // Handle phone number session storage
    if (fieldType === 'phone') {
      if (validationResult.isValid) {
        sessionStorage.setItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY, value);
      } else {
        sessionStorage.removeItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY);
      }
    }
  };

  const handleHouseTypeChange = (value: string) => {
    setFormData(prev => ({ ...prev, houseType: value }));
    handleChange('houseType' as FieldType, value);
  };

  const getAddressFromSSN = async (validSSN: string) => {
    const addressFromSSN = await getAddressDetails(Number(validSSN));
    if (addressFromSSN && !addressFromSSN.calculationStatus.error) {
      setAddressFromService(addressFromSSN);
      if (!hasFieldError('zipCode') && addressFromSSN.zipCode === formData.zipCode) {
        setFormData(prev => ({ ...prev, address: addressFromSSN.adress }));
      }
    } else {
      setFormData(prev => ({ ...prev, address: '' }));

    }
  };

  const gotoNextStep = (event: any) => {
    event.preventDefault();

    const isFormValid = validateAll(formData);

    if (isFormValid) {
      const customerInfo = {
        cprNumber: formData.ssn,
        customerName: addressFromService.customerName,
        zipCode: formData.zipCode,
        customerAddress: formData.address,
        houseType: formData.houseType
      };
      sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));

      if (NEXT_PAGE_URL_PATH) {
        window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
      }
    }
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  // To disable phone number in form toggle this to false.
  const showVolontaryPhoneNumber = true;

  const homeParams: CalculationWidgetProps = {
    headerText: enonicData.headerText,
    image: enonicData.imageInfo.image,
    smallScreenImage: enonicData.imageInfo.smallScreenImage,
    imageAltText: enonicData.imageInfo.imageAltText,
    inputLists: {
      calculationInputs: [
        ...(enonicData.selectedHouseType === '' ? [{
          type: 'inputRadio',
          wrapperClassName: 'input-group-row',
          inputListInRow: houseTypeOptions.map((option, index) => ({
            id: option.id,
            labelText: option.labelText,
            name: 'houseType', // All radio buttons must share the same name
            onChange: handleHouseTypeChange,
            onBlur: createBlurHandler('houseType' as FieldType),
            value: option.value,
            placeholder: '',
            className: '',
            labelClassName: 'input-label-text',
            invalid: hasFieldError('houseType' as FieldType),
            errorText: getFieldError('houseType' as FieldType),
            helpText: '',
            ref: index === 0 ? refs['houseType' as FieldType] : undefined // Only first radio button gets ref
          }))
        }] : []),
        ...fieldDefinitions.map(field => ({
          type: 'inputText' as const,
          isHidden: field.fieldType === 'phone' && !showVolontaryPhoneNumber,
          inputListInRow: [
            {
              ...field.ui,
              onChange: createChangeHandler(field.fieldType),
              onBlur: createBlurHandler(field.fieldType),
              value: formData[field.fieldType as keyof typeof formData],
              invalid: hasFieldError(field.fieldType),
              errorText: getFieldError(field.fieldType),
              ref: refs[field.fieldType],
              moreInfoModal: field.fieldType === 'ssn' ? {
                linkText: 'Varför frågar vi om personnummer?',
                title: 'Personnummer',
                content: getCprMoreInfoContent(),
                closeLabel: 'Stäng hjälptext',
                ariaLabel: 'Hjälp om personnummer',
                wrapperClassName: 'cpr-modal-wrapper',
                ariaDescribedBy: 'cpr-more-info'
              } : undefined
            }
          ]
        })),
      ],
      calculateBtn: {
        btnText: enonicData.calculateBtnLabel,
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: false,
        className: 'calculate-price mt-1'
      }
    },
    additionalInfoLinkText: enonicData.personalDataHandlingInfoLinkText,
    additionalInfoLink: enonicData.personalDataHandlinkUrl,
    footerMessage: enonicData.footerMessage
  };

  return <CalculationWidget {...homeParams} />;
};
